import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Mail, Briefcase, Users, Heart, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";

const Careers = () => {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const { canonical, hreflang } = buildHreflang("/careers");
  useCmsPageSEO({
    title:
      lang === "ar"
        ? "وظائف في وافاي | انضم لأفضل وكالة ويب في الكويت"
        : "Careers at Wavai | Join Kuwait's Top Web Agency",
    description:
      lang === "ar"
        ? "انضم إلى وافاي، أفضل وكالة في الكويت للويب والتجارة الإلكترونية والذكاء الاصطناعي. اطّلع على الوظائف الشاغرة وساهم في تشكيل المستقبل الرقمي للمنطقة."
        : "Join Wavai, Kuwait's top web, eCommerce and AI agency. See open roles and help shape the digital future of the region.",
    keywords: "careers Kuwait, web developer jobs Kuwait, designer jobs Kuwait, Wavai careers",
    canonical,
    hreflang,
  });

  const values = [
    {
      icon: Sparkles,
      title: t("Innovation First"),
      description: t(
        "We embrace cutting-edge technologies and AI-powered solutions to deliver exceptional results for our clients.",
      ),
    },
    {
      icon: Users,
      title: t("Collaborative Culture"),
      description: t(
        "We believe in teamwork, open communication, and supporting each other to grow both personally and professionally.",
      ),
    },
    {
      icon: Heart,
      title: t("Passion for Excellence"),
      description: t("Every project is an opportunity to push boundaries and create work we're genuinely proud of."),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-32 pb-20 lg:pt-40 lg:pb-24 relative">
          <div className="absolute inset-0 bg-gradient-hero opacity-40" />
          <div className="container px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm uppercase tracking-wider text-primary font-medium mb-4">{t("Careers")}</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
                {t("Join Kuwait's Top Web,")} <span className="text-gradient">eCommerce & AI</span> {t("Agency")}
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t(
                  "We're hiring designers, developers, strategists and growth specialists who want to build the digital future of the GCC from right here in Kuwait.",
                )}
              </p>
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-32">
          <div className="container px-6">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <h2 className="text-3xl font-display font-bold mb-4">
                {t("Work That Ships.")} <span className="text-gradient">{t("Work That Matters.")}</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {t(
                  "At Wavai, you'll work alongside senior engineers, designers and strategists on high-profile projects for the region's most recognized brands — with the autonomy to do your best work and the support to keep growing. We're a Kuwait-based agency specializing in Shopify, WordPress, Webflow and AI, serving ambitious brands across the GCC and beyond.",
                )}
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {values.map((v) => (
                <div key={v.title} className="p-6 rounded-2xl bg-card border border-border shadow-card text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <v.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-sans font-medium text-lg mb-2">{v.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-32 border-t border-border">
          <div className="container px-6">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <h2 className="text-3xl font-display font-bold mb-4">{t("Open Positions")}</h2>
              <p className="text-muted-foreground">
                {t("We're always looking for talented individuals. Check back regularly for new openings.")}
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="p-10 rounded-2xl bg-card border border-border shadow-card text-center">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                  <Briefcase className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-sans font-medium text-xl mb-2">{t("No Open Positions Currently")}</h3>
                <p className="text-muted-foreground mb-6">
                  {t(
                    "We don't have any open roles right now, but we're always interested in hearing from talented people. Send us your CV and we'll keep you in mind for future opportunities.",
                  )}
                </p>
                <Button variant="hero" asChild>
                  <a href="mailto:careers@wavai.ae">
                    <Mail className="h-4 w-4 mr-2" />
                    {t("Send Your CV")}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 lg:py-32 border-t border-border">
          <div className="container px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-display font-bold mb-4">
                {t("Think You'd Be a")} <span className="text-gradient">{t("Great Fit")}</span>?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                {t(
                  "Even if there are no current openings, we'd love to connect. Drop us an email with your CV, portfolio, and a brief note about what excites you about joining Wavai.",
                )}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button variant="hero" size="lg" asChild>
                  <a href="mailto:careers@wavai.com">
                    <Mail className="h-4 w-4 mr-2" />
                    careers@wavai.com
                  </a>
                </Button>
                <Button variant="outline" size="lg" asChild>
                  <Link to={localePath("/contact")}>{t("Contact Us")}</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Careers;
